import { useState, useEffect, useCallback, useRef } from "react";

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
  shape: "square" | "circle" | "strip";
}

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--accent))",
  "#22c55e",
  "#3b82f6",
  "#f59e0b",
  "#ec4899",
  "#8b5cf6",
];

function createParticle(id: number, originX: number, originY: number): Particle {
  const angle = (Math.random() * Math.PI * 2);
  const speed = 3 + Math.random() * 6;
  const shapes: Particle["shape"][] = ["square", "circle", "strip"];
  return {
    id,
    x: originX,
    y: originY,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed - 4,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    size: 4 + Math.random() * 6,
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 15,
    opacity: 1,
    shape: shapes[Math.floor(Math.random() * shapes.length)],
  };
}

export function useConfetti() {
  const [particles, setParticles] = useState<Particle[]>([]);
  const animationRef = useRef<number>();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const fire = useCallback((element?: HTMLElement) => {
    let originX = window.innerWidth / 2;
    let originY = window.innerHeight / 2;

    if (element) {
      const rect = element.getBoundingClientRect();
      originX = rect.left + rect.width / 2;
      originY = rect.top + rect.height / 2;
    }

    const newParticles = Array.from({ length: 40 }, (_, i) =>
      createParticle(Date.now() + i, originX, originY)
    );
    setParticles(newParticles);
  }, []);

  const isActive = particles.length > 0;
  useEffect(() => {
    if (!isActive) return;

    let lastTime = performance.now();

    function animate(currentTime: number) {
      const dt = Math.min((currentTime - lastTime) / 16.67, 3);
      lastTime = currentTime;

      setParticles(prev => {
        const updated = prev
          .map(p => ({
            ...p,
            x: p.x + p.vx * dt,
            y: p.y + p.vy * dt,
            vy: p.vy + 0.25 * dt,
            rotation: p.rotation + p.rotationSpeed * dt,
            opacity: p.opacity - 0.012 * dt,
          }))
          .filter(p => p.opacity > 0);
        return updated;
      });

      animationRef.current = requestAnimationFrame(animate);
    }

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isActive]);

  const ConfettiCanvas = useCallback(() => {
    if (particles.length === 0) return null;

    return (
      <div
        ref={containerRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          pointerEvents: "none",
          zIndex: 9999,
        }}
        data-testid="confetti-overlay"
      >
        {particles.map(p => (
          <div
            key={p.id}
            style={{
              position: "absolute",
              left: p.x,
              top: p.y,
              width: p.shape === "strip" ? p.size * 0.4 : p.size,
              height: p.shape === "strip" ? p.size * 1.5 : p.size,
              backgroundColor: p.color,
              borderRadius: p.shape === "circle" ? "50%" : "1px",
              transform: `rotate(${p.rotation}deg)`,
              opacity: p.opacity,
              willChange: "transform, opacity",
            }}
          />
        ))}
      </div>
    );
  }, [particles]);

  return { fire, ConfettiCanvas };
}
